/**
 * Contact page: the form a visitor or user sends a request with.
 *
 * Signed out, the request is stored as a public user request
 * (api/userrequests/); signed in, the form is prefilled from the profile and the
 * request is stored with the account (api/oraclase/user/request/).
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  TextField,
  Stack,
  Button,
  MenuItem,
  Typography,
  Box,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { flattenDjangoRestError, copy as userCopy } from "../utils/userFacingMessages";

const SUBJECTS = [
  {
    value: "REPORT",
    label: "Report an issue",
  },
  {
    value: "MODULE_ACCESS",
    label: "Access software module",
  },
  {
    value: "REQUEST_TRAINING",
    label: "Request training",
  },
];

const NOTLOGGEDIN_SUBJECTS = [
  {
    value: "GET_CREDENTIALS",
    label: "Get  Credentials to access the System",
  },
];

function Contact() {
  const [firstname, setFirstName] = useState("");
  const [lastname, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyname, setCompanyName] = useState("");
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [token, setToken] = useState("");
  const [showModulesDropdown, setShowModulesDropDown] = useState(false);
  const [ModuleAccess, setModuleAccess] = useState("");
  const [userId, setuserId] = useState("");
  const [moduleNames, setModuleNames] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitSucceeded, setSubmitSucceeded] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastSeverity, setToastSeverity] = useState("success");
  const [toastMessage, setToastMessage] = useState("");

  const showToast = useCallback((message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  }, []);

  const handleCloseToast = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setToastOpen(false);
  };

  useEffect(() => {
    const tk = localStorage.getItem("token");

    axios
    .get("api/modules")
    .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
            const module = res.data.map((item) => ({
                id: item.id,
                name: item.module_name,
              }));
            setModuleNames(module);
        }
    })
    .catch((err) => {
      console.error("Error in handleSubmit at axios: " + err);
    });
    if (tk !== null && tk.length > 0) {
      setToken(tk);
      axios
        .get("api/user/profile/", {
          headers: { Authorization: "Token " + tk },
        })
        .then((res) => {
          if (res.status === 200 && res.data) {
            const u = res.data;
            setFirstName(u.first_name ?? "");
            setLastName(u.last_name ?? "");
            setEmail(u.username ?? "");
            setCompanyName(u.company_name ?? "");
            setPhone(u.phone_number ?? "");
            setuserId(u.id != null ? String(u.id) : "");
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, []);
  const handleFirstNameChange = (event) => {
    setFirstName(event.target.value);
  };
  const handleLastNameChange = (event) => {
    setLastName(event.target.value);
  };
  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };
  const handlePhoneChange = (event) => {
    setPhone(event.target.value);
  };
  const handleCompanyNameChange = (event) => {
    setCompanyName(event.target.value);
  };
  const handleSubjectChange = (event) => {
    setSubject(event.target.value);
    if (event.target.value === "MODULE_ACCESS") {
      setShowModulesDropDown(true);
    } else {
      setShowModulesDropDown(false);
    }
  };
  const handleMessageChange = (event) => {
    setMessage(event.target.value);
  };
  const handleModuleAccessChange = (event) => {
    setModuleAccess(event.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || submitSucceeded) {
      return;
    }
    setSubmitting(true);
    const authHeaders =
      token.length > 0 ? { Authorization: "Token " + token } : undefined;

    try {
      if (token.length === 0) {
        const data = {
          first_name: firstname,
          last_name: lastname,
          email: email,
          phone: phone,
          company_name: companyname,
          message: message,
        };
        const res = await axios.post("api/userrequests/", data);
        if (res.status >= 200 && res.status < 300) {
          setSubmitSucceeded(true);
          showToast(userCopy.contactSent, "success");
        }
      } else {
        const data = {
          auth_user: userId,
          request_type: subject,
          message: message,
          module:
            showModulesDropdown && ModuleAccess
              ? ModuleAccess
              : null,
        };
        const res = await axios.post("api/oraclase/user/request/", data, {
          headers: authHeaders,
        });
        if (res.status >= 200 && res.status < 300) {
          setSubmitSucceeded(true);
          showToast(userCopy.contactRequestSent, "success");
        }
      }
    } catch (err) {
      console.error("Contact submit failed", err);
      const raw = err?.response?.data;
      let msg = flattenDjangoRestError(raw);
      if (!msg && err?.response?.status === 500) {
        msg = userCopy.contactServer;
      }
      if (!msg && !err.response) {
        msg = userCopy.contactNetwork;
      }
      if (!msg) {
        msg = userCopy.contactFailed;
      }
      showToast(msg, "error");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Stack
      direction="column"
      justifyContent="center"
      alignItems="center"
      spacing={4}
      marginTop="2%"
    >
      <Box sx={{ width: "100%", maxWidth: "1150px", px: 2, boxSizing: "border-box" }}>
        <Stack
          sx={{
            width: "100%",
            py: 2,
            borderRadius: 3,
            background: "linear-gradient(135deg, #f5f6f8 0%, #ecedf1 100%)",
            border: "1px solid #dde1e7",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ px: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Contact
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Send us your request and we will get back to you.
            </Typography>
          </Box>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            mt: 4,
            mb: 4,
            borderRadius: 3,
            border: "1px solid #e7ebf3",
            backgroundColor: "#ffffff",
            p: { xs: 2, md: 3 },
            boxSizing: "border-box",
          }}
        >
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                disabled={token.length !== 0}
                required
                id="firstname"
                label="First name"
                placeholder="John"
                onChange={handleFirstNameChange}
                value={firstname}
                type="text"
                fullWidth
              />
              <TextField
                disabled={token.length !== 0}
                required
                id="lastname"
                label="Last name"
                placeholder="Doe"
                onChange={handleLastNameChange}
                value={lastname}
                type="text"
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                disabled={token.length !== 0}
                required
                id="email"
                label="Email"
                placeholder="you@example.com"
                onChange={handleEmailChange}
                value={email}
                type="email"
                fullWidth
              />
              <TextField
                id="phone"
                disabled={token.length !== 0}
                label="Phone number"
                placeholder="+49 (0) 123 456789"
                onChange={handlePhoneChange}
                value={phone}
                type="tel"
                fullWidth
              />
            </Stack>

            <TextField
              id="company"
              disabled={token.length !== 0}
              label="Company name"
              placeholder="Oraclase GmbH"
              onChange={handleCompanyNameChange}
              value={companyname}
              type="text"
              fullWidth
            />

            {token.length === 0 ? (
              <TextField
                id="subject"
                select
                required
                label="Subject"
                value={subject}
                onChange={handleSubjectChange}
                fullWidth
              >
                {NOTLOGGEDIN_SUBJECTS.map((subjectOption) => (
                  <MenuItem key={subjectOption.value} value={subjectOption.value}>
                    {subjectOption.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                id="subject"
                select
                required
                label="Subject"
                value={subject}
                onChange={handleSubjectChange}
                fullWidth
              >
                {SUBJECTS.map((subjectOption) => (
                  <MenuItem key={subjectOption.value} value={subjectOption.value}>
                    {subjectOption.label}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {showModulesDropdown ? (
              <TextField
                id="modulesubject"
                select
                required
                label="Module"
                value={ModuleAccess}
                onChange={handleModuleAccessChange}
                fullWidth
              >
                {moduleNames.map((moduleOption) => (
                  <MenuItem key={moduleOption.id} value={moduleOption.id}>
                    {moduleOption.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}

            <TextField
              required
              id="message"
              label="Message"
              placeholder="Enter message"
              onChange={handleMessageChange}
              value={message}
              fullWidth
              multiline
              minRows={5}
              maxRows={8}
            />

            <Button
              variant="contained"
              startIcon={<SendIcon />}
              disabled={submitting || submitSucceeded}
              sx={{
                height: "52px",
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                alignSelf: { xs: "stretch", md: "flex-start" },
                px: 3,
              }}
              onClick={handleSubmit}
            >
              {submitSucceeded ? "Message sent" : submitting ? "Sending…" : "Send Message"}
            </Button>
          </Stack>
        </Paper>
      </Box>
      <Snackbar
        open={toastOpen}
        autoHideDuration={submitSucceeded ? 6000 : 5000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseToast} severity={toastSeverity} sx={{ width: "100%" }} variant="filled">
          {toastMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

export default Contact;
